import { Campaign } from '@/models/campaign.model'
import { officialVoiceApi, TelephonyConfig } from './voiceApi'

export type OfficialCallAvailability = 'ready' | 'coexistence' | 'notConfigured' | 'unavailable'

const CACHE_MS = 60_000
const cache = new Map<string, { until: number; value: Promise<OfficialCallAvailability> }>()
const configCache = new Map<string, { until: number; value: Promise<TelephonyConfig[]> }>()

export function invalidateOfficialCallAvailability(companyId: string, campaignId: string): void {
  configCache.delete(companyId)
  for (const key of cache.keys()) {
    if (key.startsWith(`${companyId}:${campaignId}:`)) cache.delete(key)
  }
}

async function tenantConfigs(companyId: string): Promise<TelephonyConfig[]> {
  const cached = configCache.get(companyId)
  if (cached && cached.until > Date.now()) return cached.value
  const value = officialVoiceApi.listConfigs(companyId).then(({ items }) => items || [])
  configCache.set(companyId, { until: Date.now() + CACHE_MS, value })
  return value
}

export async function officialCallAvailability(
  companyId: string,
  campaign: Campaign
): Promise<OfficialCallAvailability> {
  if (!companyId || !campaign.isOfficial || !campaign.id) return 'unavailable'
  const key = `${companyId}:${campaign.id}:${campaign.phoneNumberId || ''}`
  const cached = cache.get(key)
  if (cached && cached.until > Date.now()) return cached.value

  // The operator list is tenant-scoped. The configured phone ID also covers
  // older campaign snapshots that omit phoneNumberId in the chat state.
  const value = tenantConfigs(companyId)
    .then(async (items): Promise<OfficialCallAvailability> => {
      const config = items.find(
        item => item.provider === 'META_OFFICIAL' && item.active && item.agentId === campaign.id
      )
      if (!config) {
        if (!campaign.phoneNumberId) return 'notConfigured'
        const eligibility = await officialVoiceApi.metaEligibility(companyId, campaign.phoneNumberId)
        if (eligibility.coexistence || eligibility.state === 'UNSUPPORTED') return 'coexistence'
        return eligibility.state === 'ELIGIBLE' ? 'notConfigured' : 'unavailable'
      }
      // The saved telephony config is authoritative. A campaign snapshot may
      // still carry an older phone ID after reconnecting the official number.
      if (!config.phoneNumberId) return 'notConfigured'
      const status = await officialVoiceApi.operatorProvisioningStatus(companyId, config.id)
      if (status.coexistence || status.state === 'UNSUPPORTED') return 'coexistence'
      return status.state === 'READY' ? 'ready' : 'notConfigured'
    })
    .catch((): OfficialCallAvailability => 'unavailable')

  cache.set(key, { until: Date.now() + CACHE_MS, value })
  return value
}
